import { Region } from "../constants/regions";

export interface AppConfig {
  region: string;
  country: string;
  countryCode: string;
  currency: string;
  locale: string;
  features: {
    enabled: boolean;
    [key: string]: any;
  };
  ui: {
    theme: string;
    [key: string]: any;
  };
  [key: string]: any;
}

let cachedConfig: AppConfig | null = null;
let configLoadPromise: Promise<AppConfig> | null = null;

/**
 * Loads the configuration file for the specified region
 * @param region - The region code (ae or sa)
 * @returns Promise<AppConfig> - The loaded configuration
 */
export const loadConfig = async (region: Region): Promise<AppConfig> => {
  // If config is already cached, return it
  if (cachedConfig && cachedConfig.region === region) {
    return cachedConfig;
  }

  // If a load is already in progress, wait for it
  if (configLoadPromise) {
    return configLoadPromise;
  }

  // Start loading the config
  configLoadPromise = (async () => {
    try {
      const response = await fetch(`/configs/${region}.json`);
      console.log("region response", response);
      
      if (!response.ok) {
        throw new Error(`Failed to load config for region: ${region}`);
      }
      const config: AppConfig = await response.json();
      cachedConfig = config;
      return config;
    } catch (error) {
      console.error(`Error loading config for region ${region}:`, error);
      // Return a default config on error
      return {
        region,
        country: region === "uae" ? "UAE" : "KSA",
        countryCode: region === "uae" ? "AE" : "SA",
        currency: region === "uae" ? "AED" : "SAR",
        locale: region === "uae" ? "en-AE" : "en-SA",
        features: { enabled: true },
        ui: { theme: "default" },
      };
    } finally {
      configLoadPromise = null;
    }
  })();

  return configLoadPromise;
};

/**
 * Gets the currently cached configuration
 * @returns AppConfig | null
 */
export const getCachedConfig = (): AppConfig | null => {
  return cachedConfig;
};

/**
 * Clears the cached configuration (useful when switching regions)
 */
export const clearConfigCache = (): void => {
  cachedConfig = null;
  configLoadPromise = null;
};
